import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { modalMinWidth } from '@env/config';
import { AuthenticationService } from '@app/login/_services/authentication.service';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import { prepareDateForDb, prepareDateForForm } from '@app/_utils/date-util';
import { displayError, displayErrorFromUnknown } from '@app/_utils/error-util';
import { ListFiscalReceipt } from '../_models/list-fiscal-receipt.model';
import { IcashStore } from '../_models/icash-store.model';
import { IcashCashRegister } from '../_models/icash-cash-register.model';
import { FiscalReceiptsService } from '../_services/fiscal-receipts.service';
import { FiscalReceiptDetailDialogComponent } from './fiscal-receipt-detail-dialog/fiscal-receipt-detail-dialog.component';

/**
 * The fiscal receipts (фискални бонове) report - sales receipts read live from iCash.
 *
 * Unlike the other reports on this screen family, nothing here comes from our database: every
 * search is a call out to the iCash service. Two consequences show up below - the period is
 * capped at 31 days by iCash (the backend rejects a wider one with DAP-492), and the row count
 * arrives on the page itself rather than from a separate count endpoint.
 */
@Component({
  selector: 'app-fiscal-receipts',
  templateUrl: './fiscal-receipts.component.html'
})
export class FiscalReceiptsComponent extends BaseSortableSearchComponent<ListFiscalReceipt> implements OnInit, OnDestroy {

  // Units
  stores:                 IcashStore[] = [];
  cashRegisters:          IcashCashRegister[] = [];
  /** What the Каса dropdown shows - all of them until an Обект is picked. */
  filteredCashRegisters:  IcashCashRegister[] = [];

  // Payload
  dateObj = new Date();
  /** The points this user is tied to, from the token. Empty for an unrestricted user. */
  userTechnicalPointMobileAppIds: number[];

  // Booleans
  /** True once the Обект list has been narrowed to the user's own point(s). */
  hasTechnicalPoint = false;

  // Form
  searchForm = this.formBuilder.group({
    storeId:        [''],
    cashRegisterId: [''],
    dateFrom:       prepareDateForForm(this.dateObj),
    dateTo:         prepareDateForForm(this.dateObj),
  });

  // Observables
  searchSubscription: Subscription;
  search$ = this.searchSubject.asObservable().pipe(
    tap(() => this.dataLoaded = false),
    concatMap(() => {
      return this.fiscalReceiptsService.findAllByFilter(this.sortingPaging, this.searchForm.value,
                                                        prepareDateForDb(this.searchForm.get('dateFrom')?.value),
                                                        prepareDateForDb(this.searchForm.get('dateTo')?.value)).pipe(
        tap(page => {
          this.sortingPaging.fromRow = page.fromRow;
          this.sortingPaging.toRow = page.toRow;
          this.sortingPaging.totalElements = page.totalElements;
          this.content = page.content;
          this.dataLoaded = true;
        })
      )
    }),
    catchError(err => {
      displayError(err);
      this.dataLoaded = false;
      // Prefer what the backend said - a period longer than 31 days has its own message, and
      // showing the generic one instead would leave the operator with nothing to act on.
      this.errorMessageSubject.next(this.resolveErrorMessage(err));
      return EMPTY;
    })
  );

  constructor(
    public perms: PermissionsService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private technicalPointService: TechnicalPointService,
    private fiscalReceiptsService: FiscalReceiptsService
    ) {
      super();
      this.userTechnicalPointMobileAppIds = AuthenticationService.getEmployeeTechPointsIds();
  }

  async ngOnInit() {
    // Registers first: loadStores may pin the filter to the user's own store, and narrowing the
    // Каса list to it only works once the registers are in hand.
    await this.loadCashRegisters();
    await this.loadStores();
    this.searchSubscription = this.search$.subscribe();
    this.searchSubject.next();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  /**
   * Loads the Обект dropdown, narrowed to the user's own point when they have one.
   *
   * A КТП employee's token carries the technical points they work at, and the other restricted
   * screens (daily-service-report, list-accounting-documents) use that to pin their filter to
   * their own point. The same applies here: each point records the iCash store it sells through
   * (iCash обект ID on its edit screen), so the store is read off the point rather than guessed.
   *
   * Unlike the comparison screen this one only ever reads iCash, so a store with no point behind
   * it is still perfectly usable. Only the pinning depends on the link.
   */
  private async loadStores() {
    try {
      this.stores = await this.fiscalReceiptsService.findStores().toPromise();

      if (this.userTechnicalPointMobileAppIds.length === 0) {
        return;
      }

      const technicalPoints = await this.technicalPointService
        .findTechnicalPointsByIds(this.userTechnicalPointMobileAppIds).toPromise();

      const ownStoreIds = new Set((technicalPoints || [])
        .map(point => point.icashStoreId)
        .filter(storeId => storeId != null));

      const ownStores = this.stores.filter(store => ownStoreIds.has(store.storeId));

      if (ownStores.length === 0) {
        // Leave every store selectable rather than emptying the screen: an unconfigured point
        // should not make the report unusable, and the console line names what to go and set.
        console.warn('No iCash store is linked to the technical points of this user',
          technicalPoints?.map(point => point.shortName || point.name));
        return;
      }

      this.stores = ownStores;
      this.hasTechnicalPoint = true;
      this.searchForm.get('storeId')?.patchValue(ownStores[0].storeId);
      this.onStoreChange();
    } catch (err) {
      displayErrorFromUnknown(err);
    }
  }

  private async loadCashRegisters() {
    try {
      this.cashRegisters = await this.fiscalReceiptsService.findCashRegisters().toPromise();
      this.filteredCashRegisters = this.cashRegisters;
    } catch (err) {
      displayErrorFromUnknown(err);
    }
  }

  /**
   * Narrows the Каса dropdown to the chosen store. The selected register is cleared as well -
   * left alone, one belonging to another store would stay selected and silently filter the
   * search down to nothing.
   */
  onStoreChange() {
    const storeId = this.searchForm.get('storeId')?.value;

    this.filteredCashRegisters = storeId
      ? this.cashRegisters.filter(register => register.storeId === +storeId)
      : this.cashRegisters;

    this.searchForm.get('cashRegisterId')?.setValue('');
  }

  search() {
    this.sortingPaging.pageNumber = 1;
    this.searchSubject.next();
  }

  clearSearch() {
    this.searchForm.reset();
    this.searchForm = this.formBuilder.group({
      storeId:        [''],
      cashRegisterId: [''],
      dateFrom:       prepareDateForForm(this.dateObj),
      dateTo:         prepareDateForForm(this.dateObj),
    });

    this.filteredCashRegisters = this.cashRegisters;

    // A КТП employee stays on their own store - clearing the filters must not hand them an
    // empty Обект that would then search across every store in the list.
    if (this.hasTechnicalPoint && this.stores.length > 0) {
      this.searchForm.get('storeId')?.patchValue(this.stores[0].storeId);
      this.onStoreChange();
    }

    this.sortingPaging.pageNumber = 1;
    this.searchSubject.next();
  }

  /** Opens one receipt's article lines. The dialog fetches them itself. */
  viewReceipt(receipt: ListFiscalReceipt) {
    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    this.dialog.open(FiscalReceiptDetailDialogComponent, {
      ...modalMinWidth,
      data: {
        operationId: receipt.operationId,
        documentNumber: receipt.documentNumber
      }
    });
  }

  /** The backend's DAP code if it sent one, otherwise the generic loading message. */
  private resolveErrorMessage(err: any): string {
    const apiCode = err?.error?.code;
    const i18nKey = apiCode ? `errorCodes.${apiCode}` : null;
    const translated = i18nKey ? this.translateService.instant(i18nKey) : null;

    // instant() echoes the key back when there is no translation for it.
    if (translated && translated !== i18nKey) {
      return translated;
    }

    return this.translateService.instant('messages.errorLoadingData');
  }

}
